import React, { useState } from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import Card from "../UI/Card";
import Input from "../UI/Input";
import Button from "../UI/Button";
import SocialMediaSelector from "../SocialMediaSelector/SocialMediaSelector";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../utils/supabase";

const FormContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const SuccessMessage = styled.div`
  background-color: var(--success-color);
  color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  button {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: var(--error-color);
  color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  button {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const SubmitButton = styled(Button)`
  min-width: 150px;
`;

// n8n webhook URL
const WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL;

const PromptForm = () => {
  const { currentUser } = useAuth();
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [platformError, setPlatformError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      prompt: "",
    },
  });

  const onSubmit = async (data) => {
    if (selectedPlatforms.length === 0) {
      setPlatformError("Please select at least one social media platform");
      return;
    } else {
      setPlatformError(null);
    }

    if (!currentUser) {
      setSubmitError("You must be logged in to submit a prompt");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Prepare form data including the user_uuid for n8n to use
      const formData = {
        prompt: data.prompt,
        platforms: selectedPlatforms,
        user_uuid: currentUser.id,
        // Include user metadata that might be helpful for n8n
        provider: currentUser.app_metadata?.provider || "email",
        user_email: currentUser.email,
      };

      // Send the data to the n8n webhook for processing
      // n8n will be responsible for creating the database entry
      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log("Webhook response:", responseData);

      // Show success message
      setSubmitSuccess(true);

      // Reset form
      reset();
      setSelectedPlatforms([]);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitError("Failed to submit your prompt. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismissSuccess = () => {
    setSubmitSuccess(false);
  };

  const handleDismissError = () => {
    setSubmitError(null);
  };

  return (
    <FormContainer>
      {submitSuccess && (
        <SuccessMessage>
          <span>Your prompt has been submitted successfully!</span>
          <button onClick={handleDismissSuccess}>Dismiss</button>
        </SuccessMessage>
      )}

      {submitError && (
        <ErrorMessage>
          <span>{submitError}</span>
          <button onClick={handleDismissError}>Dismiss</button>
        </ErrorMessage>
      )}

      <Card>
        {/* <Card.Header>
          <h2>Create Your Prompt</h2>
          <p>Enter your prompt and select the social media platforms you'd like to use.</p>
        </Card.Header> */}

        <Card.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Input
              label="Your Prompt"
              multiline
              placeholder="Enter your prompt here..."
              register={register("prompt", {
                required: "Prompt is required",
                minLength: {
                  value: 10,
                  message: "Prompt should be at least 10 characters",
                },
              })}
              error={errors.prompt?.message}
            />

            <SocialMediaSelector
              selectedPlatforms={selectedPlatforms}
              onChange={setSelectedPlatforms}
              error={platformError}
            />

            <Card.Footer stackOnMobile>
              <Button
                variant="outlined"
                type="button"
                onClick={() => {
                  reset();
                  setSelectedPlatforms([]);
                  setPlatformError(null);
                }}
              >
                Clear Form
              </Button>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Prompt"}
              </SubmitButton>
            </Card.Footer>
          </form>
        </Card.Body>
      </Card>
    </FormContainer>
  );
};

export default PromptForm;
